import React, { useEffect, useRef, useState } from "react"
import PubSub from "pubsub-js"
import useT from "../hooks/useT"
import { Field, Input, Select } from "../account/ui"
import { COUNTRIES, addressMessages } from "../account/AddressForm"
import { filterAddressInput } from "aldoo-ra/Store/address-validation"
import { validateInvoiceDetails } from "aldoo-ra/Store/contact-validation"
import { INVOICE_REQUEST } from "./payment"

// the form's fields, flat; what is sent is the one shape the API, the cart
// and the order keep (aldoo-ra/Store/contact-validation validateInvoiceDetails):
// { companyName, companyId, vatNumber, contactPerson, address: { line1, city, postal_code, country } }
const EMPTY = { companyName: "", companyId: "", vatNumber: "", contactPerson: "", line1: "", city: "", postal_code: "", country: "BG" }

/** The form's fields from invoice details this step handed on before (or an older flat shape). */
const formOf = (details) => {
  if (!details) return EMPTY
  const address = details.address && typeof details.address === "object" ? details.address : { line1: details.address, city: details.city, postal_code: details.postal_code ?? details.postalCode, country: details.country }
  const text = (value) => (typeof value === "string" ? value : "")
  return {
    companyName: text(details.companyName),
    companyId: text(details.companyId),
    vatNumber: text(details.vatNumber),
    contactPerson: text(details.contactPerson),
    line1: text(address.line1),
    city: text(address.city),
    postal_code: text(address.postal_code),
    country: text(address.country) || "BG",
  }
}

const detailsOf = (form) => ({
  companyName: form.companyName,
  companyId: form.companyId,
  vatNumber: form.vatNumber,
  contactPerson: form.contactPerson,
  address: { line1: form.line1, city: form.city, postal_code: form.postal_code, country: form.country },
})

// the rule codes of aldoo-ra/Store/contact-validation, in the site's words;
// the address fields say it as an address form does
function invoiceMessages(t) {
  return {
    ...addressMessages(t),
    line1_required: t`Please enter the company address`,
    company_name_required: t`Please enter the company name`,
    company_name_invalid: t`Enter the company name as registered: 2–120 characters, no < or >`,
    company_id_required: t`Please enter the company EIK / BULSTAT`,
    tax_id_required: t`Please enter the company id or VAT number`,
    company_id_invalid: t`This is not a valid company id (an EIK / BULSTAT has 9 or 13 digits)`,
    vat_invalid: t`This is not a valid VAT number, e.g. BG123456789`,
    contact_person_invalid: t`Enter the contact person's first and last name`,
  }
}

/**
 * Checkout step "invoice-details": company invoice details stored on the
 * order (invoiceDetails). Reached in order after the address step, or from
 * the "Need an invoice?" link on the payment step (then Continue returns to
 * the payment). "Skip" clears the invoice request.
 *
 * The details are checked by the rules the API applies to them — the EIK /
 * BULSTAT and VAT number with their check digits, the address as an address
 * form checks it — and handed on in the shape the API stores.
 */
export default function InvoiceStep({ store, isActive, stepData, controller }) {
  const t = useT()
  const [form, setForm] = useState(() => formOf(stepData?.details))
  const [errors, setErrors] = useState({})
  // the address fields keep only what an address may hold, as the address form does
  const set = (key) => (e) => {
    const value = ["line1", "city", "postal_code"].includes(key) ? filterAddressInput(key, e.target.value, form.country) : e.target.value
    setForm((current) => ({ ...current, [key]: value }))
  }
  const messagesOf = (codes) => {
    const words = invoiceMessages(t)
    return Object.fromEntries(Object.entries(codes).map(([field, code]) => [field, words[code] || code]))
  }
  // a field that had an error is checked again when it is left
  const check = (key) => () => {
    if (!errors[key]) return
    const found = messagesOf(validateInvoiceDetails(detailsOf(form)).errors)
    setErrors((current) => {
      const next = { ...current }
      if (found[key]) next[key] = found[key]
      else delete next[key]
      return next
    })
  }
  const submitRef = useRef(null)
  // Opened on demand from the payment step: history holds the payment step
  const openedFromPayment = !!controller?.getData("payment-return")

  const finish = (result) => {
    if (openedFromPayment) {
      PubSub.publish("PURCHASE_FLOW_SET_DATA", { stepId: "invoice-details", data: result })
      PubSub.publish("PURCHASE_FLOW_SET_DATA", { stepId: "payment-return", data: null })
      PubSub.publish("PURCHASE_FLOW_BACK")
      return
    }
    PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: result })
  }

  const submit = () => {
    const { errors: codes, value } = validateInvoiceDetails(detailsOf(form))
    const next = messagesOf(codes)
    setErrors(next)
    if (Object.keys(next).length) return
    finish({ details: value, needsInvoice: true })
  }
  submitRef.current = submit

  const skip = () => {
    PubSub.publish("PURCHASE_FLOW_SET_DATA", { stepId: INVOICE_REQUEST, data: false })
    finish({ details: null, needsInvoice: false })
  }

  useEffect(() => {
    if (!isActive) return
    PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", { stepId: "invoice-details", canContinue: true, loading: false, onContinue: () => submitRef.current?.() })
  }, [isActive])

  const input = (key, extra = {}) => ({ name: key, value: form[key], onChange: set(key), onBlur: check(key), "aria-invalid": errors[key] ? true : undefined, ...extra })

  return (
    <form
      className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 py-6"
      data-testid="checkout-invoice"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <Field label={t`Company name`} error={errors.companyName} className="md:col-span-2">
        <Input {...input("companyName", { maxLength: 120, autoComplete: "organization" })} />
      </Field>
      <Field label={t`Company id`} error={errors.companyId}>
        <Input {...input("companyId", { maxLength: 20, inputMode: form.country === "BG" ? "numeric" : "text" })} />
      </Field>
      <Field label={t`VAT number`} error={errors.vatNumber}>
        <Input {...input("vatNumber", { maxLength: 16, placeholder: form.country === "BG" ? "BG123456789" : "" })} />
      </Field>
      <Field label={t`Company address`} error={errors.line1} className="md:col-span-2">
        <Input {...input("line1", { autoComplete: "address-line1" })} />
      </Field>
      <Field label={t`City`} error={errors.city}>
        <Input {...input("city", { autoComplete: "address-level2" })} />
      </Field>
      <Field label={t`Postal code`} error={errors.postal_code}>
        <Input {...input("postal_code", { autoComplete: "postal-code" })} />
      </Field>
      <Field label={t`Country`} error={errors.country}>
        <Select name="country" value={form.country} onChange={set("country")} autoComplete="country">
          {COUNTRIES.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t`Contact person`} error={errors.contactPerson}>
        <Input {...input("contactPerson", { maxLength: 80, autoComplete: "name" })} />
      </Field>
      <div className="md:col-span-2 flex justify-end">
        <button type="button" onClick={skip} className="text-[15px] text-text_secondary underline hover:text-text" data-testid="invoice-skip">
          {t`Skip`}
        </button>
      </div>
      <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
    </form>
  )
}
